import capitalize from 'lodash/capitalize'
import React, { useCallback, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { Button, Flex, FormOnChange, Input, Modal, Stack, Text } from '@procsea/design-system'

import { BuyerTag, SellerTag, TagType, UserType, i18nTagType } from 'src/types'

enum TagFormFields {
  NAME = 'name',
}

type BuyerOrSeller = UserType.BUYER | UserType.SELLER

type Tag<TUserType extends BuyerOrSeller> = TUserType extends UserType.BUYER ? BuyerTag : SellerTag

export interface TagFormValue {
  [TagFormFields.NAME]: string
}

const FORM_ID = 'tag-form-id'

export interface TagFormModalProps<TUserType extends BuyerOrSeller> {
  isLoading: boolean
  isTagFormModalOpen: boolean
  isEditDisabled?: boolean
  onArchive?: (id: Id) => void
  onClose: (editedTag?: Tag<TUserType>) => void
  onSubmit: (form: TagFormValue) => void
  selectedTag?: { name: string; id: Id }
  tags?: { name: string; id: Id }[]
  type: TagType
}

const TagFormModal = <TUserType extends BuyerOrSeller>({
  isLoading,
  isTagFormModalOpen,
  isEditDisabled,
  onArchive,
  onClose,
  onSubmit,
  selectedTag,
  tags,
  type,
}: TagFormModalProps<TUserType>) => {
  const { control, handleSubmit, errors, register, reset, setValue } = useForm<TagFormValue>({
    defaultValues: selectedTag,
  })
  const fields = useWatch<TagFormValue>({ control })

  const actionName = !!selectedTag ? gettext('Update') : gettext('Create')
  const modalTitle = `${actionName} - ${capitalize(i18nTagType[type])}`

  useEffect(() => {
    register(TagFormFields.NAME, {
      required: gettext('This field is required.'),
      validate: {
        isNotDuplicated: value =>
          tags?.findIndex(tag => tag.name === value) === -1 || gettext('This tag already exists'),
      },
    })

    return () => reset()
  }, [])

  const handleNameChange: FormOnChange<string> = useCallback(({ value }) => {
    setValue(TagFormFields.NAME, value, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }, [])

  const handleArchive = () => {
    if (!selectedTag) {
      throw new Error('A tag must be selected to archive it.')
    }
    onArchive?.(selectedTag.id)
  }

  return (
    <Modal
      data-e2e="tag-form-modal"
      onBackgroundClick={onClose}
      open={isTagFormModalOpen}
      afterClose={onClose}
      header={
        <Flex>
          <Text data-e2e="modal-title" bold size="large" variant="secondary">
            {modalTitle}
          </Text>

          <Button
            data-e2e="tag-cancel-button"
            icon="xmark"
            iconOutline
            text
            variant="neutral"
            onClick={() => onClose()}
          ></Button>
        </Flex>
      }
    >
      <form noValidate id={FORM_ID} name={FORM_ID} autoComplete="">
        <Stack spacing="medium">
          <Input
            autofocus={!isEditDisabled}
            data-e2e="tag-name-field"
            disabled={isEditDisabled}
            errorMessage={errors.name?.message}
            label={gettext('name')}
            onChange={handleNameChange}
            required
            value={fields.name}
          />

          <Flex>
            {typeof onArchive === 'function' && !!selectedTag ? (
              <Button
                data-e2e="tag-archive-button"
                variant="danger"
                onClick={handleArchive}
                type="button"
              >
                {gettext('Archive')}
              </Button>
            ) : (
              <div />
            )}

            <Button
              data-e2e="tag-submit-button"
              disabled={isLoading || isEditDisabled}
              form={FORM_ID}
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              type="submit"
            >
              {actionName}
            </Button>
          </Flex>
        </Stack>
      </form>
    </Modal>
  )
}

export default TagFormModal
